/**
 * useRealtimeNotifications Hook
 *
 * Subscribes to Supabase Realtime for notifications table
 * and triggers React Query refetch when new notifications arrive.
 * Updates the Header bell icon badge count instantly.
 *
 * Uses the base useRealtimeSubscription hook for:
 * - Automatic reconnection with exponential backoff
 * - Connection status tracking
 * - Error handling with Sentry integration
 * - Debounced query invalidation
 */

import { useCallback, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRealtimeSubscription, RealtimeStatus } from './useRealtimeSubscription';

interface UseRealtimeNotificationsOptions {
  userId?: string;
  enabled?: boolean;
  onNewNotification?: (notification: unknown) => void;
  onStatusChange?: (status: RealtimeStatus) => void;
}

interface UseRealtimeNotificationsResult {
  /** Current connection status */
  status: RealtimeStatus;
  /** Error if status is 'error' */
  error: Error | null;
  /** Manually unsubscribe */
  unsubscribe: () => void;
  /** Manually retry connection */
  retry: () => void;
  /** Number of reconnection attempts */
  reconnectAttempts: number;
}

export function useRealtimeNotifications(
  options: UseRealtimeNotificationsOptions = {}
): UseRealtimeNotificationsResult {
  const { userId, enabled = true, onNewNotification, onStatusChange } = options;
  const queryClient = useQueryClient();

  // Debug logging - track when hook is called and when enabled state changes
  useEffect(() => {
    console.log('[useRealtimeNotifications] Subscription state changed:', {
      userId,
      enabled,
      finalEnabled: enabled && !!userId,
      timestamp: new Date().toISOString()
    });
  }, [userId, enabled]);

  // Memoize the change handler to prevent re-subscriptions
  const handlePostgresChange = useCallback(
    (_table: string, payload: { eventType: string; new?: unknown }) => {
      console.log('[useRealtimeNotifications] 🔔 Postgres change detected!', {
        eventType: payload.eventType,
        userId,
        payload
      });

      // Invalidate unread count (Header badge)
      queryClient.invalidateQueries({ queryKey: ['unreadNotifications'] });
      // Invalidate notifications list (NotificationsPage uses this key)
      queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
      // Also invalidate workflow endpoint for backwards compatibility
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/notifications'] });
      // Invalidate needs-attention (notification types like availability_request_received show there)
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/needs-attention'] });

      console.log('[useRealtimeNotifications] ✅ Query invalidations triggered');

      // Call optional callback for new notifications
      if (payload.eventType === 'INSERT' && onNewNotification) {
        onNewNotification(payload.new);
      }
    },
    [queryClient, userId, onNewNotification]
  );

  // Memoize tables configuration
  const tables = useMemo(
    () => [
      {
        table: 'notifications',
        event: '*' as const,
        filter: userId ? `user_id=eq.${userId}` : undefined,
      },
    ],
    [userId]
  );

  // Use the base subscription hook
  const subscription = useRealtimeSubscription({
    channelName: `notifications-${userId}`,
    enabled: enabled && !!userId,
    tables,
    onPostgresChange: handlePostgresChange,
    onStatusChange,
    debounceMs: 500, // Debounce rapid updates
  });

  return {
    status: subscription.status,
    error: subscription.error,
    unsubscribe: subscription.unsubscribe,
    retry: subscription.retry,
    reconnectAttempts: subscription.reconnectAttempts,
  };
}

export default useRealtimeNotifications;
